import React from 'react';
import { Box, Typography } from '@mui/material';
import { GridComponent, ColumnsDirective, ColumnDirective, Page, Sort, Inject, RowSelectEventArgs, SortDirection } from '@syncfusion/ej2-react-grids';
import { ApprovalRequest, deriveApprovalStatus } from '../../../services/ApprovalService';
import ApprovalStatusChip from './ApprovalStatusChip';
import { ageInDays } from '../utils/approvalDate';

interface ApprovalsGridProps {
  approvals: ApprovalRequest[];
  onRowClick: (id: string) => void;
  emptyMessage?: string;
}

const ApprovalsGrid: React.FC<ApprovalsGridProps> = ({ approvals, onRowClick, emptyMessage = 'No approvals.' }) => {
  const handleRowSelect = (args: RowSelectEventArgs) => {
    const row = args.data as ApprovalRequest;
    onRowClick(row.id);
  };

  const statusTemplate = (props: ApprovalRequest) => (
    <ApprovalStatusChip status={deriveApprovalStatus(props)} />
  );

  const titleTemplate = (props: ApprovalRequest) => (
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="body2" noWrap>
        {props.entityTitle || '(untitled)'}
      </Typography>
      {props.requireComment && props.isPending && (
        <Typography variant="caption" sx={{ color: '#b45309' }}>
          Comment required
        </Typography>
      )}
    </Box>
  );

  const dateTemplate = (field: 'requestedAt' | 'respondedAt') => (props: ApprovalRequest) => {
    const value = props[field];
    return (
      <Typography sx={{ color: '#64748b', fontSize: '0.875rem' }}>
        {value ? new Date(value).toLocaleString() : '—'}
      </Typography>
    );
  };

  const ageTemplate = (props: ApprovalRequest) => (
    <Typography sx={{ color: '#64748b', fontSize: '0.875rem' }}>
      {ageInDays(props.requestedAt)}d
    </Typography>
  );

  return (
    <Box>
      <Typography variant="body2" sx={{ mb: 1, color: '#64748b', fontStyle: 'italic' }}>
        {approvals.length === 0 ? emptyMessage : 'Click any row to review and act on the approval.'}
      </Typography>
      <GridComponent
        dataSource={approvals}
        allowPaging={true}
        allowSorting={true}
        pageSettings={{ pageSize: 10, pageSizes: [10, 25, 50, 100] }}
        sortSettings={{ columns: [{ field: 'requestedAt', direction: 'Descending' as SortDirection }] }}
        height={600}
        gridLines="Both"
        rowSelected={handleRowSelect}
        cssClass="approvals-grid"
      >
        <ColumnsDirective>
          <ColumnDirective field="status" headerText="Status" width="140" textAlign="Left" allowSorting={false} template={statusTemplate} />
          <ColumnDirective field="entityTitle" headerText="Title" width="380" textAlign="Left" template={titleTemplate} />
          <ColumnDirective field="requestedAt" headerText="Requested" width="180" textAlign="Left" allowSorting={true} template={dateTemplate('requestedAt')} />
          <ColumnDirective field="respondedAt" headerText="Responded" width="180" textAlign="Left" allowSorting={true} template={dateTemplate('respondedAt')} />
          <ColumnDirective field="requestedAt" headerText="Age" width="90" textAlign="Center" allowSorting={false} template={ageTemplate} />
        </ColumnsDirective>
        <Inject services={[Page, Sort]} />
      </GridComponent>
    </Box>
  );
};

export default ApprovalsGrid;
